import { drawnFrameAnchor, drawnFrameY, footprintCorner, minCoverFor, nearestCellEdge, stampFootprint, type ManifestPixelOffset, type PropFootprint } from "daedalus-core";
import { CELLS_PER_TILE, COLLISION_CELL, SIXTEENTHS } from "../movement/step.ts";

/**
 * Run-time collision for things (ADR-042, ADR-053): published grids carry no thing footprint, so the map server
 * and the client each keep the published grid as a base and re-stamp every thing's current state over a
 * working copy whenever a thing appears, leaves or changes state. Both sides call this one function, so the
 * server's authority and the client's prediction agree.
 */

/**
 * One thing to stamp: its feet point in sixteenths, its current state's footprint (null for none), the
 * mirroring of the prop it was lowered from, which the art and the footprint share, and its appearance's pixel
 * offset, the pixels from the feet point the server holds back to the author's (undefined for an appearance
 * written without one). The footprint is laid from the frame as it is drawn, at the feet point moved by the
 * offset (`drawnFrameAnchor`, ADR-040 amendment of 2026-10-04, P1.6-T83), never from the rounded feet point
 * alone.
 */
export interface ThingStamp {
  readonly x: number;
  readonly y: number;
  readonly footprint: PropFootprint | null;
  readonly flipX: boolean;
  readonly flipY: boolean;
  readonly offset?: ManifestPixelOffset | undefined;
}

/**
 * The anchor a thing's footprint is laid from: where the frame of its current state is drawn.
 *
 * @param thing The thing.
 * @param frameWidth The pixel width of the footprint's frame.
 * @param tileSize Tile size in pixels.
 * @returns The anchor in tiles.
 */
function laidAnchor(thing: ThingStamp, frameWidth: number, tileSize: number): { x: number; y: number } {
  return drawnFrameAnchor({ x: thing.x / SIXTEENTHS, y: thing.y / SIXTEENTHS }, frameWidth, thing.offset, tileSize);
}

/**
 * Marks one thing's footprint on a grid, laid from the frame as it is drawn; a thing with no footprint marks
 * nothing. `restampThings` lays every thing with it, and so does a compile's check that reads which cells a
 * thing blocks (`editor/thing-blocks.ts`), so the two agree.
 *
 * @param cells The cells to mark.
 * @param width Map width in tiles.
 * @param height Map height in tiles.
 * @param thing The thing and its footprint.
 * @param tileSize Tile size in pixels.
 * @param cellsPerTile Collision cells per tile.
 * @param value Byte to OR in for the thing's cells.
 */
export function stampThing(cells: Uint8Array, width: number, height: number, thing: ThingStamp, tileSize: number, cellsPerTile: number, value: number): void {
  if (thing.footprint !== null) {
    const anchor = laidAnchor(thing, thing.footprint.w, tileSize);
    stampFootprint(cells, width, height, thing.footprint, { x: anchor.x, y: anchor.y, flipX: thing.flipX, flipY: thing.flipY }, tileSize, cellsPerTile, value, minCoverFor(cellsPerTile));
  }
}

/**
 * Rewrites the working grid as the base plus every thing's footprint.
 *
 * @param base The published collision cells.
 * @param cells The working cells, the same length as `base`.
 * @param width Map width in tiles.
 * @param height Map height in tiles.
 * @param things The things and their current footprints.
 * @param tileSize Tile size in pixels.
 * @param cellsPerTile Collision cells per tile.
 * @param value Byte to OR in for a thing's cells.
 * @throws Error when the working grid is not the base's size.
 */
export function restampThings(base: Readonly<Uint8Array>, cells: Uint8Array, width: number, height: number, things: readonly ThingStamp[], tileSize: number, cellsPerTile: number, value: number): void {
  if (cells.length !== base.length) {
    throw new Error(`working grid of ${cells.length} cells does not match the base of ${base.length}`);
  }
  cells.set(base);
  for (const thing of things) {
    stampThing(cells, width, height, thing, tileSize, cellsPerTile, value);
  }
}

/**
 * A rectangle of collision cells, edges included; empty when `left` exceeds `right`.
 */
export interface CellBounds {
  left: number;
  top: number;
  right: number;
  bottom: number;
}

/**
 * Empty bounds to fill.
 *
 * @returns The bounds.
 */
export function newCellBounds(): CellBounds {
  return { left: 1, top: 1, right: 0, bottom: 0 };
}

/**
 * The cells a thing's footprint can mark, as `restampThings` places it: from the art runs' pixel extent with the
 * frame laid on the cells as the stamp lays it (`footprintCorner`), mirrored as the stamp is, so every cell the
 * stamp marks lies inside (ADR-058's amendment for
 * P2.2-T7: a ray to the thing ends there).
 *
 * @param thing The thing and its footprint.
 * @param tileSize Tile size in pixels.
 * @param cellsPerTile Collision cells per tile.
 * @param out Where to write the bounds.
 * @returns False, leaving `out` as it was, when the thing stamps nothing: no footprint, no runs or flipped upright.
 */
export function thingCellBounds(thing: ThingStamp, tileSize: number, cellsPerTile: number, out: CellBounds): boolean {
  const footprint = thing.footprint;
  if (footprint === null || thing.flipY || footprint.runs.length === 0) {
    return false;
  }
  const anchor = laidAnchor(thing, footprint.w, tileSize);
  const cellPixels = tileSize / cellsPerTile;
  const corner = footprintCorner(anchor.x * tileSize, (anchor.y + 1) * tileSize, footprint.w, thing.flipX, cellPixels);
  const left = corner.left;
  const baseY = corner.bottom - (footprint.h - footprint.base);
  let first = Number.POSITIVE_INFINITY;
  let last = Number.NEGATIVE_INFINITY;
  let highest = 0;
  for (const [rows, start, end] of footprint.runs) {
    first = Math.min(first, thing.flipX ? footprint.w - 1 - end : start);
    last = Math.max(last, thing.flipX ? footprint.w - 1 - start : end);
    highest = Math.max(highest, rows);
  }
  out.left = Math.floor((left + first) / cellPixels);
  out.right = Math.floor((left + last) / cellPixels);
  out.top = Math.floor((baseY - 1 - highest) / cellPixels);
  out.bottom = Math.floor((baseY - 1) / cellPixels);
  return true;
}

/**
 * How far above a thing's feet point its footprint first blocks, in pixels: the lowest row the footprint marks,
 * `(h - base)` plus the least `rowsAboveBase` of its runs, so the answer is the same whether the footprint was derived
 * from the art (base on the art's last row, runs from 0) or painted in the footprint panel (base at the frame's
 * bottom, runs counted up from there). A footprint that blocks nothing, or none, rises nothing (ADR-058's amendment of
 * 2026-10-03).
 *
 * @param footprint The footprint, or null.
 * @returns The rise in pixels, 0 when the footprint has no runs.
 */
export function groundRise(footprint: PropFootprint | null): number {
  if (footprint === null || footprint.runs.length === 0) {
    return 0;
  }
  let lowest = Number.POSITIVE_INFINITY;
  for (const run of footprint.runs) {
    lowest = Math.min(lowest, run[0]);
  }
  return footprint.h - footprint.base + lowest;
}

/**
 * The feet y of a thing's ground, where a training dummy's feet box stands: `groundRise` above the frame's bottom
 * as the stamp lays it, on the collision cell edge nearest the bottom of the frame as it is drawn (`nearestCellEdge`,
 * `drawnFrameY`; ADR-040 amendments of 2026-10-03 for P1.6-T78 and of 2026-10-04 for P1.6-T83), the rise rounded to
 * the nearest sixteenth, so the box stands on the row the footprint blocks wherever the thing stands. A thing that
 * stamps nothing (mirrored upright, no footprint or no runs) keeps its feet
 * point. The map server and the page both call this, so the box a blow is judged against and the one the page aims
 * at agree.
 *
 * @param y The thing's feet y in sixteenths.
 * @param footprint Its standing state's footprint, or null.
 * @param flipY Whether it is mirrored upright.
 * @param tileSize Tile size in pixels.
 * @param offsetY Its appearance's pixel offset on y, 0 for an appearance written without one.
 * @returns The ground's feet y in sixteenths.
 */
export function thingGroundY(y: number, footprint: PropFootprint | null, flipY: boolean, tileSize: number, offsetY = 0): number {
  if (flipY || footprint === null || footprint.runs.length === 0) {
    return y;
  }
  const bottom = nearestCellEdge((drawnFrameY(y / SIXTEENTHS, offsetY, tileSize) + 1) * tileSize, tileSize / CELLS_PER_TILE) * COLLISION_CELL;
  return bottom - Math.round((groundRise(footprint) * SIXTEENTHS) / tileSize);
}
